import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { ChatAiAssistState } from '../../hooks/admin/useChatAiAssist';
import { ChatMessage } from '../../services/admin/FidesChatAdminService';
import { formatParticipantAddedNote } from '../../utils/chatParticipantAddedNote';
import { type ChatParticipantAddedEvent } from '../../utils/chatParticipantAddedNote';
import { Palette } from 'Palette';
import ChatLinkifiedMessageText from './ChatLinkifiedMessageText';
import ChatTimelineNote from './ChatTimelineNote';
import {
  buildContextIcon,
  buildContextLabel,
  contextsAreDifferent,
  formatMessageTime,
  shouldShowAdminSenderName,
  type ChatContext,
} from './fidesChatPanelUtils';
import { buildContextDeepLink } from '../../utils/chatDeepLinks';
import { ChatLinkTarget } from '../../utils/chatLinkCorpus';

export type ChatTimelineItem =
  | { kind: 'message'; id: string; timestamp: unknown; sortMs: number; message: ChatMessage }
  | {
      kind: 'event';
      id: string;
      timestamp: unknown;
      sortMs: number;
      event: ChatParticipantAddedEvent;
    };

interface FidesChatMessageTimelineProps {
  loading: boolean;
  displayTimeline: ChatTimelineItem[];
  currentAdminExternalId: string | null;
  contactExternalId: string;
  convId: string;
  chatLinkCorpus: ChatLinkTarget[] | null;
  chatAiAssist: ChatAiAssistState;
  onNavigate: (path: string) => void;
  messagesEndRef: React.Ref<HTMLDivElement>;
}

const FidesChatMessageTimeline: React.FC<FidesChatMessageTimelineProps> = ({
  loading,
  displayTimeline,
  currentAdminExternalId,
  contactExternalId,
  convId,
  chatLinkCorpus,
  chatAiAssist,
  onNavigate,
  messagesEndRef,
}) => (
  <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, backgroundColor: '#f6f7f9' }}>
    {loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    ) : displayTimeline.length === 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 8,
          color: 'text.secondary',
        }}
      >
        <ChatBubbleOutlineIcon sx={{ mb: 1, fontSize: 30 }} />
        <Typography variant="body2">No messages yet</Typography>
      </Box>
    ) : (
      displayTimeline.map((item, index) => {
        if (item.kind === 'event') {
          return (
            <ChatTimelineNote key={item.id}>
              {formatParticipantAddedNote(item.event, currentAdminExternalId)}
            </ChatTimelineNote>
          );
        }

        const message = item.message;
        const isAdminMessage = message.senderRole === 'admin';
        const priorMessages = displayTimeline
          .slice(0, index)
          .filter(
            (entry): entry is Extract<ChatTimelineItem, { kind: 'message' }> =>
              entry.kind === 'message',
          )
          .map((entry) => entry.message);

        const prevInvestorMessage = !isAdminMessage
          ? ([...priorMessages].reverse().find((m) => m.senderRole === 'investor') ?? null)
          : null;
        const showContextPill =
          !isAdminMessage &&
          message.context != null &&
          contextsAreDifferent(prevInvestorMessage?.context, message.context);
        const contextPillLabel = showContextPill
          ? buildContextLabel(message.context as ChatContext)
          : null;
        const contextPillLink = showContextPill
          ? buildContextDeepLink(message.context as ChatContext, contactExternalId, convId)
          : null;
        const contextPillIcon = showContextPill
          ? buildContextIcon((message.context as ChatContext | null)?.screen)
          : undefined;

        const isAiAssistTarget = !isAdminMessage && message.id === chatAiAssist.targetMessageId;
        const showAdminSenderName =
          isAdminMessage &&
          shouldShowAdminSenderName(priorMessages.concat(message), priorMessages.length);

        return (
          <React.Fragment key={message.id}>
            {showContextPill && contextPillLabel && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                {contextPillLink ? (
                  <Chip
                    size="small"
                    icon={contextPillIcon}
                    label={`Moved to: ${contextPillLabel}`}
                    onClick={() => onNavigate(contextPillLink)}
                    clickable
                    sx={{ fontSize: 11, backgroundColor: '#e8f4fd', cursor: 'pointer' }}
                  />
                ) : (
                  <Chip
                    size="small"
                    icon={contextPillIcon}
                    label={`Moved to: ${contextPillLabel}`}
                    sx={{ fontSize: 11, backgroundColor: '#e8f4fd' }}
                  />
                )}
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isAdminMessage ? 'flex-end' : 'flex-start',
                mb: 1.5,
              }}
            >
              <Tooltip
                title={!isAdminMessage ? 'Double-click for Vera assist on this message' : ''}
                disableHoverListener={isAdminMessage}
              >
                <Box
                  data-chat-message-id={message.id}
                  onDoubleClick={
                    !isAdminMessage
                      ? () => {
                          void chatAiAssist.openAssistForTarget(message.id);
                        }
                      : undefined
                  }
                  sx={{
                    maxWidth: '82%',
                    px: 1.5,
                    py: 1.1,
                    borderRadius: 2,
                    backgroundColor: isAdminMessage
                      ? '#dceefc'
                      : isAiAssistTarget
                        ? '#fff8e1'
                        : '#ffffff',
                    color: Palette.default.text,
                    boxShadow: isAiAssistTarget
                      ? '0 0 0 2px #ffc107'
                      : '0 1px 2px rgba(0,0,0,0.08)',
                    cursor: !isAdminMessage ? 'pointer' : 'default',
                  }}
                >
                  <ChatLinkifiedMessageText
                    messageId={message.id}
                    text={message.text}
                    corpus={chatLinkCorpus}
                  />
                </Box>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 0.5 }}>
                {showAdminSenderName && message.senderName ? `${message.senderName} · ` : ''}
                {formatMessageTime(message.timestamp)}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })
    )}
    <div ref={messagesEndRef} />
  </Box>
);

export default FidesChatMessageTimeline;
